"use client";

import { useState, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
// We need to create a supplier slice first! But I'll write the UI assuming it exists or use axios directly for now?
// Actually I need to create the slice. I'll create the UI file first then the slice.
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
} from "@/components/ui/sheet";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  DotsThreeVertical,
  MagnifyingGlass,
  Plus,
  Trash,
  PencilSimple,
  ClockCounterClockwise,
  Package,
} from "@phosphor-icons/react";
import { useToast } from "@/components/ui/use-toast";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent } from "@/components/ui/card";
import { Loader2 } from "lucide-react";
import axios from "axios";

// Placeholder for Slice import - will create next
// import { getSuppliers, ... } from "@/state/suppliers/supplier.slice"; 

// Ensure API URL is correct regardless of trailing slash in env
const baseUrl = import.meta.env.VITE_API_URL || 'http://localhost:8000/api/v1/';
const API_URL = baseUrl.endsWith('/') ? `${baseUrl}suppliers` : `${baseUrl}/suppliers`;

export default function SuppliersPage() {
  const { toast } = useToast();

  // Local state for now until slice is ready or using direct axios
  const [suppliers, setSuppliers] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");

  const [newSupplier, setNewSupplier] = useState({ name: "", email: "", phone: "", address: "" });
  const [editingSupplier, setEditingSupplier] = useState(null);

  const [isAddDialogOpen, setIsAddDialogOpen] = useState(false);
  const [isEditDialogOpen, setIsEditDialogOpen] = useState(false);
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
  const [supplierToDelete, setSupplierToDelete] = useState(null);

  // History State
  const [selectedSupplier, setSelectedSupplier] = useState(null);
  const [isHistoryOpen, setIsHistoryOpen] = useState(false);
  const [historyData, setHistoryData] = useState({ stocks: [], expenses: [] });
  const [isLoadingHistory, setIsLoadingHistory] = useState(false);

  // Fetch Suppliers
  const fetchSuppliers = async () => {
    setIsLoading(true);
    try {
      const res = await axios.get(API_URL, { withCredentials: true, params: { search: searchTerm } });
      if (res.data.success) {
        setSuppliers(Array.isArray(res.data.data) ? res.data.data : []);
      } else {
        setSuppliers([]);
      }
    } catch (error) {
      console.error("Fetch suppliers error:", error);
      toast({ title: "Error", description: "Failed to load suppliers", variant: "destructive" });
      setSuppliers([]);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchSuppliers();
  }, [searchTerm]);

  const handleAddSupplier = async () => {
    if (!newSupplier.name) return;
    try {
      await axios.post(API_URL, newSupplier, { withCredentials: true });
      toast({ title: "Success", description: "Supplier added successfully", className: "bg-green-50 dark:bg-green-900/30 border-green-200" });
      setNewSupplier({ name: "", email: "", phone: "", address: "" });
      setIsAddDialogOpen(false);
      fetchSuppliers();
    } catch (error) {
      toast({ title: "Error", description: error.response?.data?.message || "Failed to add", variant: "destructive" });
    }
  };

  const handleEditSupplier = async () => {
    try {
      await axios.put(`${API_URL}/${editingSupplier.id}`, editingSupplier, { withCredentials: true });
      toast({ title: "Success", description: "Updated successfully", className: "bg-green-50 dark:bg-green-900/30 border-green-200" });
      setIsEditDialogOpen(false);
      fetchSuppliers();
    } catch (error) {
      toast({ title: "Error", description: "Failed to update", variant: "destructive" });
    }
  };

  const handleDeleteSupplier = async () => {
    try {
      await axios.delete(`${API_URL}/${supplierToDelete.id}`, { withCredentials: true });
      toast({ title: "Success", description: "Deleted successfully", className: "bg-green-50 dark:bg-green-900/30 border-green-200" });
      setIsDeleteDialogOpen(false);
      fetchSuppliers();
    } catch (error) {
      toast({ title: "Error", description: error.response?.data?.message || "Failed to delete", variant: "destructive" });
    }
  };

  const loadHistory = async (supplier) => {
    setSelectedSupplier(supplier);
    setIsHistoryOpen(true);
    setIsLoadingHistory(true);
    try {
      const res = await axios.get(`${API_URL}/${supplier.id}`, { withCredentials: true });
      if (res.data.success) {
        setHistoryData(res.data.data.history);
      }
    } catch (error) {
      toast({ title: "Error", description: "Failed to load history", variant: "destructive" });
    } finally {
      setIsLoadingHistory(false);
    }
  };

  return (
    <div className="mx-auto p-4 space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold">Suppliers</h1>
          <p className="text-muted-foreground">Manage suppliers and track inventory sources</p>
        </div>
        <Dialog open={isAddDialogOpen} onOpenChange={setIsAddDialogOpen}>
          <DialogTrigger asChild>
            <Button>
              <Plus className="mr-2 h-4 w-4" />
              Add Supplier
            </Button>
          </DialogTrigger>
          <DialogContent className="sm:max-w-[500px]">
            <DialogHeader>
              <DialogTitle>Add New Supplier</DialogTitle>
            </DialogHeader>
            <div className="grid gap-4 py-4">
              <div className="grid gap-2">
                <Label htmlFor="s-name">Name *</Label>
                <Input
                  id="s-name"
                  value={newSupplier.name}
                  onChange={(e) => setNewSupplier({ ...newSupplier, name: e.target.value })}
                />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div className="grid gap-2">
                  <Label htmlFor="s-email">Email</Label>
                  <Input
                    id="s-email"
                    value={newSupplier.email}
                    onChange={(e) => setNewSupplier({ ...newSupplier, email: e.target.value })}
                  />
                </div>
                <div className="grid gap-2">
                  <Label htmlFor="s-phone">Phone</Label>
                  <Input
                    id="s-phone"
                    value={newSupplier.phone}
                    onChange={(e) => setNewSupplier({ ...newSupplier, phone: e.target.value })}
                  />
                </div>
              </div>
              <div className="grid gap-2">
                <Label htmlFor="s-address">Address</Label>
                <Input
                  id="s-address"
                  value={newSupplier.address}
                  onChange={(e) => setNewSupplier({ ...newSupplier, address: e.target.value })}
                />
              </div>
            </div>
            <DialogFooter>
              <Button onClick={handleAddSupplier}>Save Supplier</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>

      <div className="flex items-center space-x-2 bg-white dark:bg-slate-900 p-2 border w-fit">
        <MagnifyingGlass className="w-5 h-5 text-gray-500 dark:text-gray-500" />
        <Input
          placeholder="Search suppliers..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          className="border-none shadow-none focus-visible:ring-0 w-[300px]"
        />
      </div>

      <Card>
        <div className="p-0">
          {isLoading && suppliers.length === 0 ? (
            <div className="flex items-center justify-center h-64">
              <Loader2 className="h-8 w-8 animate-spin text-primary" />
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Name</TableHead>
                  <TableHead>Contact</TableHead>
                  <TableHead>Value Supplied</TableHead>
                  <TableHead>Paid</TableHead>
                  <TableHead>Balance</TableHead>
                  <TableHead className="text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {suppliers.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={6} className="text-center h-32 text-muted-foreground">
                      No suppliers found.
                    </TableCell>
                  </TableRow>
                ) : (
                  suppliers.map((supplier) => (
                    <TableRow key={supplier.id}>
                      <TableCell className="font-medium">
                        <div>{supplier.name}</div>
                        <div className="text-xs text-muted-foreground">{supplier.address}</div>
                      </TableCell>
                      <TableCell>
                        <div className="text-sm">{supplier.email}</div>
                        <div className="text-sm text-muted-foreground">{supplier.phone}</div>
                      </TableCell>
                      <TableCell>GHS {parseFloat(supplier.totalSupplied || 0).toFixed(2)}</TableCell>
                      <TableCell>GHS {parseFloat(supplier.totalPaid || 0).toFixed(2)}</TableCell>
                      <TableCell>
                        {supplier.balance > 0 ? (
                          <Badge variant="destructive">Due: GHS {parseFloat(supplier.balance).toFixed(2)}</Badge>
                        ) : (
                          <span className="text-green-600 text-sm">Settled</span>
                        )}
                      </TableCell>
                      <TableCell className="text-right">
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild>
                            <Button variant="ghost" className="h-8 w-8 p-0">
                              <DotsThreeVertical className="h-4 w-4" />
                            </Button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent align="end">
                            <DropdownMenuLabel>Actions</DropdownMenuLabel>
                            <DropdownMenuItem onClick={() => loadHistory(supplier)}>
                              <ClockCounterClockwise className="mr-2 h-4 w-4" /> History
                            </DropdownMenuItem>
                            <DropdownMenuItem onClick={() => {
                              setEditingSupplier(supplier);
                              setIsEditDialogOpen(true);
                            }}>
                              <PencilSimple className="mr-2 h-4 w-4" /> Edit
                            </DropdownMenuItem>
                            <DropdownMenuSeparator />
                            <DropdownMenuItem onClick={() => {
                              setSupplierToDelete(supplier);
                              setIsDeleteDialogOpen(true);
                            }} className="text-red-600">
                              <Trash className="mr-2 h-4 w-4" /> Delete
                            </DropdownMenuItem>
                          </DropdownMenuContent>
                        </DropdownMenu>
                      </TableCell>
                    </TableRow>
                  )))}
              </TableBody>
            </Table>
          )}
        </div>
      </Card>

      {/* Edit Dialog */}
      <Dialog open={isEditDialogOpen} onOpenChange={setIsEditDialogOpen}>
        <DialogContent className="sm:max-w-[500px]">
          <DialogHeader>
            <DialogTitle>Edit Supplier</DialogTitle>
          </DialogHeader>
          {editingSupplier && (
            <div className="grid gap-4 py-4">
              <div className="grid gap-2">
                <Label htmlFor="e-s-name">Name</Label>
                <Input
                  id="e-s-name"
                  value={editingSupplier.name}
                  onChange={(e) => setEditingSupplier({ ...editingSupplier, name: e.target.value })}
                />
              </div>
              {/* ... other fields similar to add ... */}
              <div className="grid grid-cols-2 gap-4">
                <div className="grid gap-2">
                  <Label htmlFor="e-s-email">Email</Label>
                  <Input
                    id="e-s-email"
                    value={editingSupplier.email}
                    onChange={(e) => setEditingSupplier({ ...editingSupplier, email: e.target.value })}
                  />
                </div>
                <div className="grid gap-2">
                  <Label htmlFor="e-s-phone">Phone</Label>
                  <Input
                    id="e-s-phone"
                    value={editingSupplier.phone}
                    onChange={(e) => setEditingSupplier({ ...editingSupplier, phone: e.target.value })}
                  />
                </div>
              </div>
              <div className="grid gap-2">
                <Label htmlFor="e-s-address">Address</Label>
                <Input
                  id="e-s-address"
                  value={editingSupplier.address}
                  onChange={(e) => setEditingSupplier({ ...editingSupplier, address: e.target.value })}
                />
              </div>
            </div>
          )}
          <DialogFooter>
            <Button onClick={handleEditSupplier}>Save Changes</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Dialog */}
      <Dialog open={isDeleteDialogOpen} onOpenChange={setIsDeleteDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Delete Supplier</DialogTitle>
            <DialogDescription>
              Are you sure? This will soft delete the supplier profile.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setIsDeleteDialogOpen(false)}>Cancel</Button>
            <Button variant="destructive" onClick={handleDeleteSupplier}>Delete</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* History Sheet */}
      <Sheet open={isHistoryOpen} onOpenChange={setIsHistoryOpen}>
        <SheetContent className="sm:max-w-[600px] overflow-y-auto">
          <SheetHeader>
            <SheetTitle>Supplier History</SheetTitle>
            <SheetDescription>Supply and payment records for {selectedSupplier?.name}</SheetDescription>
          </SheetHeader>
          {isLoadingHistory ? (
            <div className="flex justify-center py-10"><Loader2 className="animate-spin" /></div>
          ) : (
            <div className="mt-6 space-y-6">
              <div className="grid grid-cols-3 gap-4">
                <Card>
                  <CardContent className="pt-6">
                    <div className="text-xl font-bold">GHS {parseFloat(selectedSupplier?.totalSupplied || 0).toFixed(2)}</div>
                    <p className="text-xs text-muted-foreground">Total Supplied</p>
                  </CardContent>
                </Card>
                <Card>
                  <CardContent className="pt-6">
                    <div className="text-xl font-bold">GHS {parseFloat(selectedSupplier?.totalPaid || 0).toFixed(2)}</div>
                    <p className="text-xs text-muted-foreground">Total Paid</p>
                  </CardContent>
                </Card>
                <Card>
                  <CardContent className="pt-6">
                    <div className={`text-xl font-bold ${selectedSupplier?.balance > 0 ? 'text-red-500' : 'text-green-500'}`}>
                      GHS {parseFloat(selectedSupplier?.balance || 0).toFixed(2)}
                    </div>
                    <p className="text-xs text-muted-foreground">Balance Due</p>
                  </CardContent>
                </Card>
              </div>

              {/* Stock History */}
              <div>
                <h3 className="font-semibold mb-2 flex items-center gap-2">
                  <Package className="h-4 w-4" /> Stock Supplied
                </h3>
                <div className="space-y-2">
                  {historyData.stocks?.length === 0 ? (
                    <p className="text-muted-foreground text-sm">No stock records.</p>
                  ) : (
                    historyData.stocks?.map((stock) => (
                      <div key={stock.id} className="text-sm border p-2 rounded flex justify-between">
                        <div>
                          <div className="font-medium">{stock.Product?.title || 'Unknown Product'}</div>
                          <div className="text-xs text-muted-foreground">{new Date(stock.createdAt).toLocaleDateString()}</div>
                        </div>
                        <div className="text-right">
                          <div>{stock.quantity} units</div>
                          {stock.data?.cost && <div className="text-xs text-muted-foreground">@{stock.data.cost}/unit</div>}
                        </div>
                      </div>
                    ))
                  )}
                </div>
              </div>

              {/* Expenses/Payments History */}
              <div>
                <h3 className="font-semibold mb-2 mt-4">Payments Made</h3>
                <div className="space-y-2">
                  {historyData.expenses?.length === 0 ? (
                    <p className="text-muted-foreground text-sm">No payment records.</p>
                  ) : (
                    historyData.expenses?.map((exp) => (
                      <div key={exp.id} className="text-sm border p-2 rounded flex justify-between">
                        <div>
                          <div className="font-medium">{exp.description || 'Payment'}</div>
                          <div className="text-xs text-muted-foreground">{new Date(exp.createdAt).toLocaleDateString()}</div>
                        </div>
                        <div className="text-right font-mono">
                          - GHS {parseFloat(exp.amount).toFixed(2)}
                        </div>
                      </div>
                    ))
                  )}
                </div>
              </div>
            </div>
          )}
        </SheetContent>
      </Sheet>
    </div>
  );
}
